import React, { useState, useEffect } from 'react';
import { updateHistory, deleteHistory } from '../services/historyService';
import { getDisplayErrorMessage } from '../services/apiError';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import HistorySettingsPanel from './HistorySettingsPanel';
import CloseButton from './CloseButton';
import RichTextEditor from './RichTextEditor';
import ErrorMessage from './ErrorMessage';

const EditHistoryForm = () => {
  const { historyId } = useParams();
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState({
    name: ''
  });
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    setError(null);
    setErrors({});
    
    const validationErrors = {};

    if (name.trim() === "") {
        validationErrors.name = "Name is required";
    }

    if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
    }
    
    setIsLoading(true);

    try {
      await updateHistory(historyId, { name, isPublic, description });
      navigate(-1);
    } catch (err) {
      if (err.message && err.message.includes("UNIQUE constraint failed")) {
        setError("History with this name already exists");
      } else {
        setError(getDisplayErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setError(null);

    setIsLoading(true);
    try {
      await deleteHistory(historyId);
      navigate('/');
    } catch (err) {
      setError(getDisplayErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  const validateField = (value) => {
    let error = "";
    
    if (value.trim() === "") {
      error = "Name is required";
    }

    setErrors(prev => ({ ...prev, name: error }));
  };

  useEffect(() => {
    if (location.state && location.state.history) {
      setName(location.state.history.name);
      setDescription(location.state.history.description);
      setIsPublic(location.state.history.isPublic);
    }
  }, [location.state]);

  return (
    <div className="h-[calc(100vh-64px)] overflow-y-auto flex items-start justify-center bg-gray-100 py-8">
      <div className="bg-gray-200 mx-auto w-full max-w-2xl rounded shadow-md py-6 relative">
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
        <div className="flex justify-between px-4 items-center">
          <button
            onClick={ () =>  setIsModalOpen(true)}
            className="bg-red-500 w-11 h-11 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-red-600 hover:border-2 hover:border-red-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="white" className="size-7">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
          <CloseButton onClick={() => navigate(-1)} disabled={isLoading}/>
        </div>
        <form className="bg-gray-300 rounded px-16 pt-6 pb-8 my-4 flex flex-col shadow-lg" onSubmit={ handleSubmit }>
          <h1 className="text-center text-3xl mb-6 bg-gray-200 border-2 border-gray-400 rounded-xl py-3">Editing history</h1>
          <div className="mb-4">
            <input 
              placeholder="Enter new name for history" 
              type="text" 
              className="w-full rounded p-1" 
              value={ name } 
              onChange={ (e) => setName(e.target.value) }
              onBlur={ (e) => validateField(e.target.value)}
            />
            {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
          </div>

          <div className="mb-4">
            <RichTextEditor
              content={description}
              onChange={setDescription}
              placeholder="Enter description for history"
            />
          </div>

          <div className="mb-6">
            <input 
              type="checkbox" 
              className="mr-2" 
              checked={ isPublic } 
              onChange={ (e) => setIsPublic(e.target.checked) }
              id="public-check"
            />
          <label htmlFor="public-check">Public?</label>
          </div>
          <button
            disabled={ isLoading }
            type="submit" className="bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
          {isLoading ? 'Processing...' : 'Update history'}
          </button>
        </form>

        <HistorySettingsPanel historyId={historyId}/>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow-lg">
            <h3 className="text-xl font-bold mb-4">Confirmation of deletion</h3>
            <p className="mb-4">Are you sure you want to delete this history?</p>
            <button 
              onClick={handleDelete}
              className="bg-red-500 text-white rounded p-2 hover:bg-red-600 border-2 hover:border-red-800"
            >
              Yes, delete
            </button>
            <button 
              className="bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 ml-4"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
        )
      }
    </div>
  )
}

export default EditHistoryForm;